import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import clientService from '../../services/ClientService.js';
// #305 — load pending compliance reviews into the same widget so the
// reviewer notices when something needs their approval.
import emailCampaignService from '../../services/EmailCampaignService.js';

const MAX_PREVIEW = 3;
const COMPLIANCE_FLAG_ON = () =>
  import.meta.env?.VITE_USE_COMPLIANCE_APPROVAL === 'true';

function clientName(clients, clientId) {
  const c = clients.find(c => c.id === clientId);
  if (!c) return clientId;
  return `${c.firstName} ${c.lastName}`;
}

/**
 * RemindersWidget — compact bell-icon button with count badge + popover.
 *
 * Issue #298: previously this component rendered an inline expanded block
 * (bell + "Reminders" heading + count + empty-state copy + "View All" link)
 * directly in the top nav, which got visually crushed and truncated. Now
 * matches the Notifications dropdown pattern: icon-only button as the
 * always-visible trigger, content lives in a popover that opens on click.
 */
export default function RemindersWidget() {
  const [reminders, setReminders] = useState([]);
  const [clients, setClients] = useState([]);
  // #305 — campaigns awaiting the current user's compliance review.
  const [pendingReviews, setPendingReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const tasks = [
          clientService.getUpcomingReminders(),
          clientService.getClients(),
        ];
        // #305 — only fetch pending-review campaigns when the compliance
        // flag is on. Behaviour-neutral fallback otherwise (zero new
        // network traffic for non-compliance users).
        if (COMPLIANCE_FLAG_ON()) {
          tasks.push(loadPendingReviews());
        }
        const [fetchedReminders, fetchedClients, fetchedReviews] = await Promise.all(tasks);
        setReminders(fetchedReminders || []);
        setClients(fetchedClients || []);
        setPendingReviews(fetchedReviews || []);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Pulled out so a failure on the compliance side doesn't break the
  // entire load — Promise.all rejects on first rejection, so this
  // function swallows its own error and returns []. Widget rendering
  // must never depend on compliance services being healthy.
  async function loadPendingReviews() {
    try {
      const user = await getCurrentUser();
      if (!user?.userId) return [];
      // Same query the inbox page uses (getUserCampaigns returns
      // campaigns owned by the user; in the self-review MVP the owner
      // IS the reviewer, so this works. Multi-person reviewer assignment
      // changes this query in #316).
      const all = await emailCampaignService.getUserCampaigns(user.userId, 200);
      return (all || []).filter((c) => c.complianceStatus === 'pending_review');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[RemindersWidget] pending-review lookup failed:', err);
      return [];
    }
  }

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const preview = reminders.slice(0, MAX_PREVIEW);
  const pendingReviewPreview = pendingReviews.slice(0, MAX_PREVIEW);
  const count = reminders.length + pendingReviews.length;
  const hasPendingReviews = pendingReviews.length > 0;

  return (
    <div className="relative" ref={popoverRef} aria-label="Reminders Widget">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={`${count} upcoming reminders`}
        aria-expanded={isOpen}
        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md relative"
      >
        <svg
          data-testid="bell-icon"
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        <span
          className={
            count > 0
              ? 'absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] font-medium bg-red-500 text-white rounded-full flex items-center justify-center'
              : 'absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] font-medium bg-gray-400 text-white rounded-full flex items-center justify-center'
          }
        >
          {count}
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
          {/* #305 — Compliance review section. Renders above the regular
              reminders block when the reviewer has pending campaigns.
              Linked items navigate to the reviewer inbox. */}
          {hasPendingReviews && (
            <div className="border-b border-gray-200">
              <div className="p-4 border-b border-gray-100 bg-amber-50/40">
                <h3 className="text-sm font-semibold text-amber-900">
                  Compliance review ({pendingReviews.length})
                </h3>
              </div>
              <ul>
                {pendingReviewPreview.map((c) => (
                  <li
                    key={c.id}
                    className="hover:bg-amber-50/30 border-b border-gray-100 last:border-b-0"
                  >
                    <Link
                      to="/app/communications/review"
                      onClick={() => setIsOpen(false)}
                      className="block p-4"
                    >
                      <span className="block text-sm font-medium text-gray-900">
                        {c.name || 'Untitled campaign'}
                      </span>
                      {c.subject && (
                        <span className="block text-xs text-gray-500 mt-1">
                          {c.subject}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="p-4 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Reminders</h3>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <p className="p-4 text-sm text-gray-500">Loading…</p>
            ) : reminders.length === 0 ? (
              <p className="p-4 text-sm text-gray-500">No upcoming reminders</p>
            ) : (
              <ul>
                {preview.map((r) => (
                  <li
                    key={r.id}
                    className="p-4 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    <span className="block text-sm font-medium text-gray-900">
                      {clientName(clients, r.clientId)}
                    </span>
                    {r.notes && (
                      <span className="block text-xs text-gray-500 mt-1">{r.notes}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Link
            to="/app/clients"
            className="block p-3 text-center text-sm text-blue-600 hover:bg-gray-50 border-t border-gray-200 rounded-b-lg"
            onClick={() => setIsOpen(false)}
          >
            View All
          </Link>
        </div>
      )}
    </div>
  );
}
